/**
 * The upload dialog's state: the shared attachment draft with the catalog's rules, and the text status of each
 * landed file, asked again every 2 s while any is pending. There is no route to take an upload back; a file that
 * never becomes a material is removed by the server's sweep after a day, so "remove" only drops the row here.
 */

import { useMutation } from '@tanstack/react-query';

import { FILE_STATUS_MAX, UPLOAD_TOO_LARGE, useFileStatuses, useUploadCatalogFile } from '@/api/queries/materials';
import type { CatalogFileOut, TextStatus } from '@/api/types/materials';
import { useAttachmentDraft, type AttachmentRules } from '@/ui/Attachments';

export const MATERIAL_RULES: AttachmentRules = {
  maxFiles: FILE_STATUS_MAX,
  maxBytes: 50 * 1024 * 1024,
  accept:
    '.pdf,.docx,.pptx,.md,.markdown,.txt,application/pdf,text/markdown,text/plain,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  acceptedNames: /\.(pdf|docx|pptx|md|markdown|txt)$/i,
  acceptedTypes: [
    'application/pdf',
    'text/markdown',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  hint: 'Or drop files here. PDF, Word, PowerPoint, Markdown or text, up to 50 MB each, ten at a time.',
  tooLarge: UPLOAD_TOO_LARGE,
  wrongType: 'Not a PDF, Word, PowerPoint, Markdown or text file',
  screenshots: false,
};

export function useMaterialUploads() {
  const keep = useMutation({ meta: { quiet: true }, mutationFn: async (_id: string) => undefined });
  const draft = useAttachmentDraft<CatalogFileOut>({ rules: MATERIAL_RULES, upload: useUploadCatalogFile(), remove: keep });
  const landed = draft.drafts.flatMap((d) => (d.state === 'done' && d.attachment ? [{ key: d.key, file: d.attachment }] : []));
  const statuses = useFileStatuses(landed.filter((l) => l.file.text_status === 'pending').map((l) => l.file.id));
  const statusOf = (file: CatalogFileOut): TextStatus =>
    statuses.data?.find((s) => s.id === file.id)?.text_status ?? file.text_status;
  return { draft, landed, statusOf };
}
